import { Event, EventLookup, LookupSupplement } from '../types';

/**
 * Variazioni di prezzo legate alle risposte del form: supplementi (+) e
 * riduzioni (−), fisse in € o in percentuale della quota della tipologia.
 * Replica il calcolo del server (App\Services\LookupSupplements): stesso
 * ambito, stessa visibilità, stessa base. Il server ricalcola comunque
 * tutto: qui serve per mostrare il totale giusto e per ripartire le rate
 * sull'importo che il server si aspetta.
 */

export interface AppliedSupplement {
  lookupId: number;
  name: string;
  /** Con segno: positivo = supplemento, negativo = riduzione */
  amount: number;
}

type Values = Record<string | number, any>;

const round2 = (n: number) => Math.round(n * 100) / 100;

const valueOf = (values: Values, lookupId: number) => values[lookupId] ?? values[String(lookupId)];

/** Variazione che scatta per quella risposta (null = nessuna). */
export const supplementFor = (lookup: EventLookup, value: any): LookupSupplement | null => {
  const sups = lookup.supplements ?? [];
  if (!sups.length || value === undefined || value === null || value === '') return null;
  const type = lookup.lookup.type;
  if (type === 'checkbox') {
    const checked = value === true || value === 1 || value === '1';
    return checked ? sups.find(s => s.lookup_option_id === null) ?? null : null;
  }
  if (type === 'select' || type === 'radio') {
    return sups.find(s => String(s.lookup_option_id) === String(value)) ?? null;
  }
  return null;
};

/** Variazione legata a un'opzione specifica (per le etichette). */
export const supplementForOption = (lookup: EventLookup, optionId: number | null): LookupSupplement | null =>
  (lookup.supplements ?? []).find(s => s.lookup_option_id === optionId) ?? null;

/** Importo con segno applicato a una quota (stessa formula del server). */
export const resolveAmount = (sup: LookupSupplement, base: number): number => {
  const amount = Number(sup.amount) || 0;
  return sup.amount_type === 'percent' ? round2((base * amount) / 100) : round2(amount);
};

const inScope = (lookup: EventLookup, forParticipant: boolean) => {
  const scope = lookup.participant_scope ?? 'referente';
  return forParticipant ? scope === 'both' || scope === 'participants' : scope !== 'participants';
};

const isVisible = (lookup: EventLookup, event: Event, values: Values, isEnte: boolean, depth = 0): boolean => {
  if (lookup.show_if_ente === 1 && !isEnte) return false;
  if (!lookup.visibile_condition || !lookup.condition_lookup_id || lookup.condition_value === null) return true;
  if (String(valueOf(values, lookup.condition_lookup_id)) !== String(lookup.condition_value)) return false;
  const parent = event.lookups.find(l => l.lookup_id === lookup.condition_lookup_id);
  return !parent || depth > 10 || isVisible(parent, event, values, isEnte, depth + 1);
};

/** @param base quota della tipologia di quella persona (base delle percentuali) */
export const computeSupplements = (
  event: Event | null | undefined,
  values: Values,
  isEnte: boolean,
  forParticipant: boolean,
  base: number
): AppliedSupplement[] => {
  if (!event?.lookups) return [];
  const out: AppliedSupplement[] = [];
  for (const lookup of event.lookups) {
    if (!lookup.supplements?.length || !inScope(lookup, forParticipant)) continue;
    if (!forParticipant && !isVisible(lookup, event, values, isEnte)) continue;
    const sup = supplementFor(lookup, valueOf(values, lookup.lookup_id));
    if (sup) out.push({ lookupId: lookup.lookup_id, name: lookup.lookup.name, amount: resolveAmount(sup, base) });
  }
  return out;
};

export const sumSupplements = (list: AppliedSupplement[]) => round2(list.reduce((acc, s) => acc + s.amount, 0));

/** Totale di una persona: quota + variazioni, mai sotto zero. */
export const personTotal = (base: number, list: AppliedSupplement[]) => Math.max(0, round2(base + sumSupplements(list)));

/** Quota della tipologia oggi (stessa legge di sconto del server). */
export const tipologiaAmount = (event: Event | null | undefined, priceId: number | undefined): number => {
  if (!priceId || !event?.prices) return 0;
  const price = event.prices.find(p => p.id === priceId);
  if (!price || price.gratis === 1) return 0;
  const isDiscountValid = !!price.discounted_price
    && !!price.valid_start_date
    && new Date() < new Date(price.valid_start_date + 'T23:59:59');
  const value = isDiscountValid ? price.discounted_price : price.default_price;
  return parseFloat((value as string) || '0');
};

type Translate = (key: string, fallback: string, vars?: Record<string, string | number>) => string;

/** Etichetta accanto a una risposta: "(+ 70,00 €)", "(− 20,00 €)", "(− 10%)". */
export const supplementSuffix = (sup: LookupSupplement | null, t: Translate, fmtMoney: (n: number) => string): string => {
  if (!sup) return '';
  const amount = Number(sup.amount) || 0;
  if (!amount) return '';
  const shown = sup.amount_type === 'percent' ? `${Math.abs(amount)}%` : fmtMoney(Math.abs(amount));
  return amount > 0
    ? t('supplemento.etichetta', '(+ {{amount}})', { amount: shown })
    : t('supplemento.riduzione', '(− {{amount}})', { amount: shown });
};

/** Importo con segno per il riepilogo: "+ 70,00 €" / "− 20,00 €". */
export const signedMoney = (n: number, fmtMoney: (n: number) => string) =>
  `${n < 0 ? '−' : '+'} ${fmtMoney(Math.abs(n))}`;
